import { cn } from "cn";
import * as React from "react";
import { frostCoverage, isThawPointer, thawPoint } from "../lib/frost";
import { createFrostController, type FrostController } from "../lib/frost-controller";
import { createFrostPainter, type FrostPainter } from "../lib/frost-paint";
import { createShakeApplier } from "../lib/frost-shake";

/**
 * «Ледяной взрыв» поверх родителя (ТЗ §113): при `active` экран вздрагивает,
 * по краям вспыхивает холодный свет и нарастает матовый лёд с трещинами; при
 * `active = false` лёд оттаивает к краям. Центр остаётся чистым.
 *
 * Родитель должен быть `position: relative`: оверлей занимает его целиком, не
 * перехватывает нажатия и не двигает раскладку. Тряска — `transform` элемента
 * `shakeTarget` на 400 мс, потом возвращается прежний; без `shakeTarget` тряски
 * нет. Пока льда нет, кадры не
 * запрашиваются, а слой с размытием не показан.
 *
 * Компонент не знает домена: ему нужно одно булево. Числа вида — в `lib/frost`.
 */

const FLASH_FILL_PCT = 14.5;

function FrostOverlay({
  active,
  shakeTarget,
  thawRadius,
  thawTarget,
  className,
  ...props
}: {
  active: boolean;
  /** Элемент, который вздрагивает при заморозке. Без него тряски нет. */
  shakeTarget?: React.RefObject<HTMLElement | null>;
  /** Радиус круга оттаивания под курсором мыши, px (§118). Без него кругов нет. */
  thawRadius?: number;
  /**
   * Элемент, над которым курсор оттаивает лёд. Без него кругов нет. Элемент рефа
   * читается при монтировании и при смене самого пропа: если `ref.current`
   * заменится позже, слушатели останутся на прежнем элементе.
   */
  thawTarget?: React.RefObject<HTMLElement | null>;
} & Omit<React.ComponentProps<"div">, "children">) {
  const shakeTargetRef = React.useRef(shakeTarget);
  shakeTargetRef.current = shakeTarget;
  const painterRef = React.useRef<FrostPainter | null>(null);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const iceRef = React.useRef<HTMLDivElement>(null);
  const flashRef = React.useRef<HTMLDivElement>(null);
  const linesRef = React.useRef<HTMLCanvasElement>(null);
  const controllerRef = React.useRef<FrostController | null>(null);

  React.useEffect(() => {
    const root = rootRef.current;
    const ice = iceRef.current;
    const flash = flashRef.current;
    const lines = linesRef.current;
    if (root === null || ice === null || flash === null || lines === null) {
      return;
    }
    const applyShake = createShakeApplier(() => shakeTargetRef.current?.current ?? null);
    const painter = createFrostPainter({ root, frost: ice, lines });
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    const controller = createFrostController({
      now: () => performance.now(),
      requestFrame: (callback) => requestAnimationFrame(callback),
      cancelFrame: (id) => cancelAnimationFrame(id),
      reducedMotion: () => reducedMotion.matches,
      coverage: () => {
        const { width, height } = painter.size();
        return frostCoverage(width, height);
      },
      size: () => painter.size(),
      draw: (front) => painter.draw(front),
      setFlash: (opacity) => {
        flash.style.opacity = String(opacity);
      },
      setShake: applyShake,
      drawHoles: (holes, radius) => painter.drawHoles(holes, radius),
    });
    controllerRef.current = controller;
    painterRef.current = painter;

    const observer = new ResizeObserver(() => {
      painter.resize(root.clientWidth, root.clientHeight);
      controller.invalidate();
    });
    observer.observe(root);

    return () => {
      observer.disconnect();
      controller.destroy();
      controllerRef.current = null;
      painterRef.current = null;
    };
  }, []);

  React.useEffect(() => {
    controllerRef.current?.setThawRadius(thawRadius ?? 0);
  }, [thawRadius]);

  React.useEffect(() => {
    const target = thawTarget?.current ?? null;
    const root = rootRef.current;
    if (target === null || root === null) {
      return;
    }
    const onMove = (event: PointerEvent): void => {
      const controller = controllerRef.current;
      // Пока круг не нужен, геометрию не читаем: pointermove идёт на каждое движение мыши.
      if (controller === null || !controller.wantsPointer()) {
        return;
      }
      const point = thawPoint(event, root.getBoundingClientRect());
      if (point !== null) {
        controller.pointer(point);
      }
    };
    const onLeave = (event: PointerEvent): void => {
      if (isThawPointer(event.pointerType)) {
        controllerRef.current?.pointer(null);
      }
    };
    target.addEventListener("pointermove", onMove, { passive: true });
    target.addEventListener("pointerleave", onLeave, { passive: true });
    return () => {
      target.removeEventListener("pointermove", onMove);
      target.removeEventListener("pointerleave", onLeave);
      controllerRef.current?.pointer(null);
    };
  }, [thawTarget]);

  React.useEffect(() => {
    // Цвета читаются заново на каждом переходе: тема могла смениться с прошлого раза.
    painterRef.current?.refreshColors();
    controllerRef.current?.set(active);
  }, [active]);

  return (
    <div
      ref={rootRef}
      data-slot="frost-overlay"
      data-active={active}
      aria-hidden="true"
      className={cn("pointer-events-none absolute inset-0 overflow-hidden", className)}
      {...props}
    >
      {/* Матовый лёд: размытие и высветление того, что под ним; форму задаёт маска. */}
      <div
        ref={iceRef}
        data-slot="frost-ice"
        className="absolute inset-0 bg-frost backdrop-blur-[5px] backdrop-brightness-[1.1] backdrop-saturate-[0.45]"
        style={{ display: "none", maskSize: "100% 100%", WebkitMaskSize: "100% 100%" }}
      />
      <div
        ref={flashRef}
        data-slot="frost-flash"
        className="absolute inset-0"
        style={{
          opacity: 0,
          boxShadow: "inset 0 0 220px 70px var(--frost-flash)",
          backgroundColor: `color-mix(in srgb, var(--frost-flash) ${FLASH_FILL_PCT}%, transparent)`,
        }}
      />
      <canvas
        ref={linesRef}
        data-slot="frost-cracks"
        className="absolute inset-0 size-full opacity-85"
      />
    </div>
  );
}

export { FrostOverlay };
